import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import DoorCatalogue from './components/DoorCatalogue';

const features = [
  { icon: 'ri-shield-check-line', title: 'Secured by Design Certified', desc: 'Every Hallmark door is Secured by Design certified, featuring a high-security anti-snap, anti-pick, anti-drill, anti-bump euro cylinder and a multi-point locking system tested to PAS 24 and Document Q standards.' },
  { icon: 'ri-fire-line', title: 'FD30 Fire Door Option', desc: 'Hallmark manufacture a dedicated FD30 rated composite fire door range — the same stunning GRP skins and colour options, certified to 30-minute fire resistance. Ideal for integral garage doors and certain flat entrance doors.' },
  { icon: 'ri-contrast-2-line', title: 'HallMAX PRO Double Rebate', desc: 'The Signature Deluxe range uses Hallmark\'s new HallMAX PRO 70mm twin double-rebate frame — engineered for superior weather sealing, thermal performance, and rigidity far beyond a standard composite door frame.' },
  { icon: 'ri-palette-line', title: 'Over 2,000 Colour Options', desc: 'Choose from standard colours, premium woodgrain foils, or the new 2025 Deluxe Matte range including Deep Lagoon, Silent Midnight, and Agate Grey. Bespoke RAL colours available through the Hallmark Colour Lab.' },
  { icon: 'ri-leaf-line', title: '19% More Thermally Efficient', desc: 'Hallmark composite doors are independently tested to be 19% more thermally efficient than a timber door — and significantly better than uPVC. The high-density polyurethane foam core delivers outstanding U-values year-round.' },
  { icon: 'ri-award-line', title: "UK's No.1 Door Brand 5 Years Running", desc: "Hallmark Doors & Panels has been voted the UK's best composite door brand five consecutive years by installers and industry bodies. Trusted in over 500,000 UK homes, backed by verified Trustpilot and Google reviews." },
];

const faqs = [
  { q: 'What makes a Hallmark composite door different from other brands?', a: 'Hallmark composite doors use a high-impact GRP (Glass Reinforced Polyester) skin over a high-density polyurethane foam core — not the cheaper expanded foam used in lower-cost alternatives. The GRP skin is moulded at higher tolerances, meaning the woodgrain texture and panel detail is sharper and more realistic. Hallmark have been voted UK\'s No.1 door brand five years consecutively by industry professionals.' },
  { q: 'What is the HallMAX PRO and do I need it?', a: 'The HallMAX PRO is Hallmark\'s premium 70mm twin double-rebate door system — introduced in 2025. It features a double weatherseal, superior frame rigidity, and enhanced thermal performance. It is standard on all Signature Deluxe doors. For most Surrey homes, the standard Hallmark composite door frame delivers excellent performance; the PRO frame is recommended for exposed or north-facing entrances.' },
  { q: 'How much does a Hallmark composite door cost installed in Caterham?', a: 'Installed prices for a Hallmark composite door in Caterham and across Surrey typically start from around £1,200 for a Traditional collection door, rising to £1,800–£2,400+ for the Signature Deluxe range with the HallMAX PRO frame. We provide free written quotations with full itemised pricing — no hidden extras. Call 07852 636 300 or complete the form above.' },
  { q: 'Can I see the door in person before I order?', a: 'Yes — during your free home survey we bring colour samples and glass examples so you can see exactly how your chosen combination looks at your actual entrance in natural light. We strongly recommend this before committing to a colour, as colours can look very different on screen versus in situ.' },
  { q: 'Do Hallmark composite doors come with a guarantee?', a: 'Yes. Every Hallmark composite door comes with a manufacturer\'s guarantee on the door slab and GRP skin. We back this with our own installation guarantee. Any post-installation issues with hardware, seals, or fit are covered by Ensure Windows directly — you always have a local point of contact in Caterham to call.' },
  { q: 'Can I have a Hallmark door as a rear or side entrance door?', a: 'Absolutely. Hallmark composite doors are fitted as front doors, rear kitchen doors, garage-to-house doors, and side access doors across Surrey. The Cottage & Country range — particularly the Lancia 4 and Cortina 4 — are especially popular as rear garden doors. Stable door configurations (upper and lower halves open independently) are also available in the Cottage range.' },
  { q: 'How long does installation take?', a: 'Most Hallmark composite door installations are completed in half a day. Where new frame work, threshold adjustment, or brickmould is required it may run to a full day. Our installation team leaves your home clean and tidy, adjusts all hardware, and demonstrates the locking system before completing the job.' },
  { q: 'Are Hallmark composite doors suitable for conservation areas or listed buildings?', a: 'Some conservation areas and listed buildings in and around Caterham have restrictions on door replacements. We will advise you on any applicable restrictions during your free survey — and in many cases Hallmark\'s timber-texture GRP doors satisfy conservation requirements due to their authentic appearance.' },
];

const testimonials = [
  { name: 'Jennifer Marsh', location: 'Caterham, Surrey', text: 'Had the Tuscan 3 fitted in Anthracite Grey with the Danthorpe Cross glass. It has completely transformed our front entrance — so many compliments from the neighbours. The installation team were professional, tidy, and finished well ahead of schedule.', init: 'JM' },
  { name: 'Mark & Lisa Dawson', location: 'Whyteleafe, Surrey', text: 'We chose the Lancia 4 in Chartwell Green for our rear kitchen door — couldn\'t be happier. The door looks exactly like a real timber board door but we\'ve had zero maintenance issues in 18 months. Ensure Windows were honest about pricing with no hidden extras whatsoever.', init: 'MD' },
  { name: 'Alan Grover', location: 'Coulsdon, South London', text: 'Went for the Seville in Deep Lagoon — one of the new Deluxe Matte colours. It is an absolute statement door and I get comments every week. Very impressed with the Hallmark quality and the knowledge of the Ensure Windows team throughout.', init: 'AG' },
];

const processSteps = [
  { n: '1', title: 'Free Home Survey', desc: 'We visit your home, measure your frame precisely, and discuss designs, colours, and glass options at no charge and no obligation.' },
  { n: '2', title: 'Written Quotation', desc: 'A clear, itemised written quote within 48 hours. We list everything — door, frame, hardware, glazing, and installation.' },
  { n: '3', title: 'Manufacture', desc: 'Your door is manufactured to your exact specification, typically delivered within 3–5 weeks from order confirmation.' },
  { n: '4', title: 'Expert Installation', desc: 'Our installers fit your door to a high standard, adjust all hardware, and demonstrate the locking system before they leave.' },
];

export default function CompositeDoors() {
  const formRef = useRef<HTMLElement>(null);
  const [prefilledDoor, setPrefilledDoor] = useState('');
  const [prefilledColour, setPrefilledColour] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', doorModel: '', colour: '', message: '' });

  useEffect(() => {
    document.title = 'Composite Doors Caterham Surrey | Ensure Windows';
    return () => { document.title = 'Ensure Windows Caterham | Premium Windows & Doors Surrey'; };
  }, []);

  const handleRequestQuote = (doorName: string, colourName: string) => {
    setPrefilledDoor(doorName);
    setPrefilledColour(colourName);
    setForm((f) => ({ ...f, doorModel: doorName, colour: colourName }));
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 80);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await fetch('https://readdy.ai/api/form/d7216fbk1jkj467r5pj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(form as Record<string, string>).toString(),
      });
    } finally {
      setSubmitted(true);
      setSubmitting(false);
    }
  };

  return (
    <main>
      <Navbar />

      {/* ── Hero ────────────────────────────────────── */}
      <section className="relative min-h-[82vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/cc6e46f4f43d2369a080a1b5401b5d2e.jpg"
            alt="Composite doors Caterham Surrey"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-transparent"></div>
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-24">
          <div className="max-w-2xl">
            <nav
              className="flex items-center gap-2 text-xs text-stone-400 mb-6"
              aria-label="Breadcrumb"
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              <Link to="/" className="hover:text-amber-400 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/products" className="hover:text-amber-400 transition-colors cursor-pointer">Products</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white">Composite Doors</span>
            </nav>
            <span
              className="inline-block text-amber-400 text-xs font-bold tracking-widest uppercase border border-amber-400/50 px-4 py-1.5 rounded-full mb-5"
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              Caterham · Surrey · South London
            </span>
            <h1
              className="text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-5"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Composite Doors in Caterham &amp; Surrey
            </h1>
            <p
              className="text-lg text-stone-200 leading-relaxed mb-8"
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              12 door designs across Classic, Cottage, and Contemporary collections. Made to measure, expert-fitted, and backed by a 10-year guarantee. Free survey and written quote for all Surrey homes.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#door-catalogue"
                className="bg-amber-600 hover:bg-amber-500 text-white font-semibold px-8 py-4 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                Browse the Catalogue
              </a>
              <a
                href="tel:07852636300"
                className="border border-white/60 hover:border-white text-white hover:bg-white/10 font-semibold px-8 py-4 rounded-md transition-all whitespace-nowrap cursor-pointer"
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                Call 07852 636 300
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Quick Stats ─────────────────────────────── */}
      <section className="bg-zinc-900 py-8">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-4 divide-x divide-white/10">
          {[
            { icon: 'ri-door-open-line', v: '26+', l: 'Door Styles' },
            { icon: 'ri-palette-line', v: '2,000+', l: 'Colour Options' },
            { icon: 'ri-shield-check-line', v: 'PAS 24', l: 'Security Rated' },
            { icon: 'ri-award-line', v: '5× Best Brand', l: 'UK Award Winner' },
          ].map((b) => (
            <div key={b.l} className="flex flex-col items-center text-center px-4">
              <div className="w-7 h-7 flex items-center justify-center mb-2">
                <i className={`${b.icon} text-amber-400 text-lg`}></i>
              </div>
              <span
                className="text-white text-xl font-bold"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {b.v}
              </span>
              <span
                className="text-stone-400 text-xs mt-0.5"
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                {b.l}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ── What is a Composite Door ─────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span
              className="text-amber-600 text-xs font-bold tracking-widest uppercase"
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              Why Hallmark?
            </span>
            <h2
              className="text-4xl font-bold text-zinc-900 mt-3 mb-5"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              The UK&apos;s No.1 Composite Door — Voted Best Brand 5 Years Running
            </h2>
            <div
              className="space-y-4 text-zinc-500 text-sm leading-relaxed"
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              <p>
                Hallmark Doors &amp; Panels manufacture the UK&apos;s most specified composite door range.
                At the core of every door is a <strong>high-density polyurethane foam slab</strong> wrapped in a high-impact
                GRP (Glass Reinforced Polyester) skin — moulded to replicate the authentic grain and texture of timber
                with a precision that cheaper alternatives simply cannot match.
              </p>
              <p>
                Hallmark doors have been independently tested to be <strong>19% more thermally efficient than timber</strong> —
                and significantly outperform uPVC. The 2025 range introduces the <strong>HallMAX PRO double-rebate frame</strong>,
                a 70mm twin-seal system that represents the pinnacle of composite door engineering available anywhere in the UK.
              </p>
              <p>
                We are authorised Hallmark installers serving Caterham, Whyteleafe, Purley, Coulsdon, Oxted, and surrounding
                Surrey areas. Every door is surveyed, supplied, and fitted by our own installation team — no subcontractors.
              </p>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4">
              {[
                { label: 'GRP Skin', value: 'High-Impact Polyester' },
                { label: 'Core', value: 'High-Density Foam' },
                { label: 'Frame Option', value: 'HallMAX PRO 70mm' },
                { label: 'Security', value: 'Secured by Design' },
                { label: 'Energy', value: '19% Better Than Timber' },
                { label: 'Brand Award', value: 'No.1 UK — 5 Yrs' },
              ].map((s) => (
                <div key={s.label} className="bg-stone-50 rounded-lg p-4 border border-stone-100">
                  <p
                    className="text-zinc-400 text-xs uppercase tracking-wide"
                    style={{ fontFamily: "'Raleway', sans-serif" }}
                  >
                    {s.label}
                  </p>
                  <p
                    className="text-zinc-800 font-bold text-sm mt-1"
                    style={{ fontFamily: "'Raleway', sans-serif" }}
                  >
                    {s.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="w-full h-[480px] rounded-lg overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=composite%20door%20close%20up%20detail%20glass%20reinforced%20polymer%20skin%20woodgrain%20texture%20high%20quality%20finish%20dark%20green%20chartwell%20elegant%20surrey%20home%20entrance%20warm%20evening%20light&width=700&height=480&seq=cd-intro-02&orientation=portrait"
              alt="Hallmark composite door detail and quality finish"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* ── DOOR CATALOGUE (Interactive) ─────────────── */}
      <DoorCatalogue onRequestQuote={handleRequestQuote} />

      {/* ── Features Grid ───────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span
              className="text-amber-600 text-xs font-bold tracking-widest uppercase"
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              What You Get
            </span>
            <h2
              className="text-4xl font-bold text-zinc-900 mt-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Features &amp; Benefits of Every Door We Install
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.title} className="flex items-start gap-4 p-6 rounded-lg border border-stone-100 bg-stone-50/50">
                <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center bg-amber-50 rounded-lg border border-amber-100">
                  <i className={`${f.icon} text-amber-600 text-xl`}></i>
                </div>
                <div>
                  <h3
                    className="text-base font-bold text-zinc-800 mb-1.5"
                    style={{ fontFamily: "'Raleway', sans-serif" }}
                  >
                    {f.title}
                  </h3>
                  <p
                    className="text-zinc-500 text-sm leading-relaxed"
                    style={{ fontFamily: "'Raleway', sans-serif" }}
                  >
                    {f.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Process ─────────────────────────────────── */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span
              className="text-amber-600 text-xs font-bold tracking-widest uppercase"
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              How It Works
            </span>
            <h2
              className="text-4xl font-bold text-zinc-900 mt-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              From First Contact to Fitted Door
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((s, i) => (
              <div key={s.n} className="relative text-center">
                {i < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[calc(50%+2.5rem)] right-0 border-t-2 border-dashed border-amber-200 z-0"></div>
                )}
                <div
                  className="relative z-10 w-16 h-16 flex items-center justify-center bg-amber-600 rounded-full mx-auto mb-5 text-white text-2xl font-bold"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {s.n}
                </div>
                <h3
                  className="text-base font-bold text-zinc-800 mb-2"
                  style={{ fontFamily: "'Raleway', sans-serif" }}
                >
                  {s.title}
                </h3>
                <p
                  className="text-zinc-500 text-sm leading-relaxed"
                  style={{ fontFamily: "'Raleway', sans-serif" }}
                >
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span
              className="text-amber-600 text-xs font-bold tracking-widest uppercase"
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              Customer Reviews
            </span>
            <h2
              className="text-4xl font-bold text-zinc-900 mt-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              What Our Customers Say
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-stone-50 rounded-lg p-7 border border-stone-100">
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-amber-500 text-sm"></i>
                    </div>
                  ))}
                </div>
                <p
                  className="text-zinc-600 text-sm leading-relaxed mb-6 italic"
                  style={{ fontFamily: "'Raleway', sans-serif" }}
                >
                  &ldquo;{t.text}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-600 rounded-full text-white text-xs font-bold">
                    {t.init}
                  </div>
                  <div>
                    <p
                      className="text-zinc-800 text-sm font-semibold"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    >
                      {t.name}
                    </p>
                    <p
                      className="text-zinc-400 text-xs"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    >
                      {t.location}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────── */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span
              className="text-amber-600 text-xs font-bold tracking-widest uppercase"
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              FAQ
            </span>
            <h2
              className="text-4xl font-bold text-zinc-900 mt-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-stone-200 rounded-lg overflow-hidden bg-white">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer hover:bg-stone-50 transition-colors"
                >
                  <span
                    className="font-semibold text-zinc-800 text-sm pr-4"
                    style={{ fontFamily: "'Raleway', sans-serif" }}
                  >
                    {faq.q}
                  </span>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'} text-amber-600`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 bg-stone-50 border-t border-stone-100">
                    <p
                      className="text-zinc-500 text-sm leading-relaxed pt-3"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    >
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Quote Form ──────────────────────────────── */}
      <section
        id="quote-form"
        ref={formRef as React.RefObject<HTMLElement>}
        className="py-20"
        style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2d1f0e 100%)' }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-14 items-start">
            <div>
              <span
                className="text-amber-400 text-xs font-bold tracking-widest uppercase"
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                Get Started
              </span>
              <h2
                className="text-4xl font-bold text-white mt-4 mb-5"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {prefilledDoor
                  ? `Request a Quote for the ${prefilledDoor}`
                  : 'Get Your Free Composite Door Quote'}
              </h2>
              <p
                className="text-stone-300 text-sm leading-relaxed mb-8"
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                Tell us which door you&apos;re interested in and we&apos;ll arrange a free home survey at a time that suits
                you. Written quotation within 48 hours, no obligation.
              </p>
              <div className="flex flex-col gap-4 mb-8">
                {[
                  { icon: 'ri-phone-line', text: '07852 636 300', href: 'tel:07852636300' },
                  { icon: 'ri-mail-line', text: 'sales@ensurewindows.co.uk', href: 'mailto:sales@ensurewindows.co.uk' },
                  { icon: 'ri-map-pin-2-line', text: 'Seven House, Town End, Caterham CR3 5UG', href: '#' },
                ].map((item) => (
                  <a
                    key={item.text}
                    href={item.href}
                    className="flex items-start gap-3 cursor-pointer group"
                    rel={item.href === '#' ? 'nofollow' : undefined}
                  >
                    <div className="w-9 h-9 flex items-center justify-center bg-amber-600/20 rounded-md flex-shrink-0">
                      <i className={`${item.icon} text-amber-400`}></i>
                    </div>
                    <span
                      className="text-stone-300 group-hover:text-amber-400 transition-colors text-sm mt-2"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    >
                      {item.text}
                    </span>
                  </a>
                ))}
              </div>
              {/* Areas covered */}
              <div className="flex flex-wrap gap-2">
                {['Caterham', 'Whyteleafe', 'Purley', 'Coulsdon', 'Redhill', 'Oxted', 'Reigate', 'Warlingham', 'Godstone'].map(
                  (area) => (
                    <span
                      key={area}
                      className="text-xs text-stone-400 border border-stone-600 rounded-full px-3 py-1"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    >
                      {area}
                    </span>
                  )
                )}
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-8">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center bg-amber-600/20 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-amber-400 text-3xl"></i>
                  </div>
                  <h3
                    className="text-white text-2xl font-bold mb-2"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    Thank You!
                  </h3>
                  <p
                    className="text-stone-300 text-sm"
                    style={{ fontFamily: "'Raleway', sans-serif" }}
                  >
                    We&apos;ll be in touch within 24 hours to arrange your free survey.
                  </p>
                </div>
              ) : (
                <>
                  <h3
                    className="text-white text-xl font-bold mb-6"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    Request a Free Survey &amp; Quote
                  </h3>
                  <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="Your Full Name"
                      className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-400 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    />
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="Email Address"
                      className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-400 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    />
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="Phone Number"
                      className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-400 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        name="doorModel"
                        value={form.doorModel}
                        onChange={handleChange}
                        placeholder="Door design (e.g. Harlington)"
                        className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-400 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                        style={{ fontFamily: "'Raleway', sans-serif" }}
                      />
                      <input
                        name="colour"
                        value={form.colour}
                        onChange={handleChange}
                        placeholder="Colour (e.g. Anthracite Grey)"
                        className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-400 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                        style={{ fontFamily: "'Raleway', sans-serif" }}
                      />
                    </div>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={3}
                      maxLength={500}
                      placeholder="Tell us about your project — front door, back door, any frame work needed?"
                      className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-400 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400 resize-none"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    />
                    <p
                      className="text-right text-stone-500 text-xs -mt-2"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    >
                      {form.message.length}/500
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-amber-600 hover:bg-amber-500 text-white font-semibold py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    >
                      {submitting ? 'Sending...' : 'Request Free Survey & Quote'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
